import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ScriptureResponse } from "@/hooks/useFindScripture";
import { v4 as uuidv4 } from "uuid";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/AuthProvider";
import { Json } from "@/integrations/supabase/types";

// Function to call the Supabase edge function
const getScriptures = async (topic: string): Promise<ScriptureResponse[]> => {
  const { data, error } = await supabase.functions.invoke("scripture-navigator", {
    body: {
      action: "findScriptures",
      topic
    }
  });

  if (error) {
    console.error("Error fetching scriptures:", error);
    throw new Error(error.message);
  }

  if (data?.verses?.length) {
    // Transform the API response to match our ScriptureResponse structure
    return data.verses.map((verse: any) => ({
      id: uuidv4(),
      reference: verse.reference,
      text: verse.text,
      context: verse.explanation,
    }));
  }

  return [];
};

// Function to get commentary for a specific verse
const getCommentary = async (verseReference: string): Promise<string> => {
  const { data, error } = await supabase.functions.invoke("scripture-navigator", {
    body: {
      action: "getCommentary",
      verseReference
    }
  });

  if (error) {
    console.error("Error fetching commentary:", error);
    throw new Error(error.message);
  }

  return data?.commentary || "Commentary not available.";
};

export const useScriptureNavigator = () => {
  const { user } = useAuth();
  const [scriptures, setScriptures] = useState<ScriptureResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStates, setLoadingStates] = useState<Record<string, { commentary: boolean }>>({});
  const [topic, setTopic] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);

  const fetchScriptures = async (searchTopic: string) => {
    setTopic(searchTopic);
    try {
      setIsLoading(true);
      const results = await getScriptures(searchTopic);
      setScriptures(results);
    } catch (error) {
      console.error("Failed to fetch scriptures:", error);
      toast({
        title: "Error",
        description: "Failed to fetch scriptures. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetCommentary = async (id: string) => {
    const scripture = scriptures.find(s => s.id === id);
    if (!scripture) return;

    // Set loading state for this specific scripture's commentary
    setLoadingStates(prev => ({
      ...prev,
      [id]: { commentary: true }
    }));

    try {
      const commentary = await getCommentary(scripture.reference);
      
      setScriptures(prev => prev.map(s => {
        if (s.id === id) {
          return { ...s, commentary };
        }
        return s;
      }));
    } catch (error) {
      console.error("Failed to fetch commentary:", error);
    } finally {
      // Clear loading state
      setLoadingStates(prev => ({
        ...prev,
        [id]: { commentary: false }
      }));
    }
  };

  const saveItem = async (type: 'verse' | 'commentary' | 'guidance', scripture: ScriptureResponse) => {
    try {
      if (!user) {
        toast({
          title: "Authentication Required",
          description: "Please sign in to save content.",
          variant: "destructive",
        });
        return;
      }

      setIsSaving(true);
      
      // Create the content object for storage
      const contentObj = {
        type,
        reference: scripture.reference,
        text: scripture.text,
        context: scripture.context,
        commentary: type === 'commentary' ? scripture.commentary : undefined,
        topic,
        createdAt: new Date().toISOString()
      };

      console.log(`Saving ${type} content:`, contentObj);

      const { error } = await supabase
        .from('saved_responses')
        .insert({
          tool_type: 'scripture_navigator',
          title: `${type.charAt(0).toUpperCase() + type.slice(1)}: ${scripture.reference}`,
          content: contentObj as Json,
          user_id: user.id
        });

      if (error) throw error;

      toast({
        title: "Saved Successfully",
        description: `${type.charAt(0).toUpperCase() + type.slice(1)} has been saved.`,
      });
    } catch (error) {
      console.error(`Error saving ${type}:`, error);
      toast({
        title: "Error",
        description: `Failed to save ${type}. Please try again later.`,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const saveScriptures = async () => {
    if (!scriptures.length || !topic) {
      toast({
        title: "No Content",
        description: "There are no scriptures to save.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);
      
      if (!user) {
        toast({
          title: "Authentication Required",
          description: "Please sign in to save scriptures.",
          variant: "destructive",
        });
        return;
      }

      // Create content object for storage
      const contentObj = {
        topic,
        scriptures: scriptures.map(scripture => ({
          reference: scripture.reference,
          text: scripture.text,
          context: scripture.context,
          commentary: scripture.commentary
        })),
        createdAt: new Date().toISOString()
      };
      
      console.log("Saving all scriptures:", contentObj);

      const { error } = await supabase
        .from('saved_responses')
        .insert({
          tool_type: 'scripture_navigator',
          title: `Scripture Navigator: ${topic}`,
          content: contentObj as Json,
          user_id: user.id
        });

      if (error) throw error;

      toast({
        title: "Saved Successfully",
        description: "Your scripture navigation has been saved to your dashboard.",
      });
    } catch (error) {
      console.error('Error saving scriptures:', error);
      toast({
        title: "Error",
        description: "Failed to save scriptures. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    scriptures,
    isLoading,
    isSaving,
    topic,
    fetchScriptures,
    loadingStates,
    handleGetCommentary,
    saveScriptures,
    saveItem
  };
};
